import React, { useEffect, useState } from 'react';
import {
  AlertTriangle,
  BarChart3,
  LayoutDashboard,
  LogOut,
  Menu,
  ShieldAlert,
  Users,
  X,
} from 'lucide-react';
import adminService from './services/adminService';
import BrandLogo from './components/BrandLogo';
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';
import UserList from './pages/UserList';

export default function App() {
  const [auth, setAuth] = useState(() => adminService.getStoredAuth());
  const [tab, setTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [error, setError] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [focusUserId, setFocusUserId] = useState(null);

  const handleNavigateToUser = (userId) => {
    setFocusUserId(userId);
    setTab('users');
  };

  useEffect(() => {
    if (!auth) return;
    setError('');
    adminService
      .getOverviewStats()
      .then((data) => setStats(data))
      .catch((err) => {
        if (err.status === 401) {
          adminService.logout();
          setAuth(null);
          return;
        }
        setError(err.message);
      });
  }, [auth]);

  const confirmLogout = () => {
    adminService.logout();
    setAuth(null);
    setTab('dashboard');
    setStats(null);
    setShowLogoutDialog(false);
  };

  const selectTab = (nextTab) => {
    setTab(nextTab);
    setSidebarOpen(false);
  };

  if (!auth) {
    return <Login onLoginSuccess={(data) => setAuth(data)} />;
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-950">
      {sidebarOpen && (
        <button
          aria-label="Close menu"
          className="fixed inset-0 z-30 bg-slate-950/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-40 flex w-64 flex-col bg-[#0F172A] text-white shadow-2xl transition-transform lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex h-16 items-center justify-between border-b border-white/10 px-6">
          <div className="flex items-center gap-3">
            <BrandLogo className="h-8 w-8" showText />
          </div>
          <button
            className="rounded-lg p-2 text-slate-400 hover:bg-white/10 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          >
            <X size={20} />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-1">
          <NavButton
            active={tab === 'dashboard'}
            icon={<LayoutDashboard size={20} />}
            label="Dashboard"
            onClick={() => selectTab('dashboard')}
          />
          <NavButton
            active={tab === 'users'}
            icon={<Users size={20} />}
            label="Users"
            onClick={() => selectTab('users')}
          />
          <NavButton
            active={tab === 'reports'}
            icon={<AlertTriangle size={20} />}
            label="Reports"
            onClick={() => selectTab('reports')}
          />
          <NavButton
            active={tab === 'risk'}
            icon={<ShieldAlert size={20} />}
            label="Risk Monitor"
            onClick={() => selectTab('risk')}
          />
          <NavButton
            active={tab === 'analytics'}
            icon={<BarChart3 size={20} />}
            label="Analytics"
            onClick={() => selectTab('analytics')}
          />
        </nav>

        <div className="border-t border-white/10 px-2 py-3 space-y-1">
          <button
            className="flex w-full items-center gap-3 rounded-lg px-4 py-2.5 text-left text-sm font-medium text-white/60 transition hover:bg-white/[0.08] hover:text-white"
            onClick={() => setShowLogoutDialog(true)}
          >
            <LogOut size={20} />
            Logout
          </button>
        </div>
      </aside>

      <div className="min-h-screen lg:pl-64">
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b border-[#E2E8F0] bg-white px-8">
          <div className="flex items-center gap-4">
            <button
              className="rounded-lg p-2 text-slate-600 hover:bg-[#F1F5F9] lg:hidden"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu size={20} />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-[#0F172A] tracking-tight capitalize">
                {tab}
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="hidden items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 md:flex">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              <span className="text-xs font-medium text-emerald-700">
                Live API
              </span>
            </div>
            <div className="flex items-center gap-2 pl-3 pr-2 py-1.5 rounded-lg hover:bg-[#F1F5F9] cursor-pointer">
              <div className="w-8 h-8 rounded-full bg-[#0F172A] flex items-center justify-center">
                <Users size={14} className="text-white" />
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold text-[#0F172A] leading-tight">
                  {(auth.user?.name || auth.user?.email || 'Admin').replace(/^Temporary\s*/i, '')}
                </p>
                <p className="text-[10px] text-[#94A3B8]">
                  Administrator
                </p>
              </div>
            </div>
          </div>
        </header>

        <main className="mx-auto w-full max-w-7xl p-6">
          {error && (
            <div className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-[#D91F26]">
              {error}
            </div>
          )}
          {tab === 'users' ? (
            <UserList focusUserId={focusUserId} onStatsChanged={() => { setFocusUserId(null); adminService.getOverviewStats().then(setStats); }} />
          ) : (
            <Dashboard onNavigateToUser={handleNavigateToUser} section={tab} stats={stats} />
          )}
        </main>
      </div>

      {showLogoutDialog && (
        <ConfirmDialog
          confirmLabel="Log out"
          message="You will need to sign in again before managing users, reports, and analytics."
          onCancel={() => setShowLogoutDialog(false)}
          onConfirm={confirmLogout}
          title="Log out of admin?"
        />
      )}
    </div>
  );
}

function ConfirmDialog({ confirmLabel, message, onCancel, onConfirm, title }) {
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="w-full max-w-md rounded-xl border border-[#E2E8F0] bg-white p-6 shadow-2xl">
        <div className="mb-5 flex items-center gap-3">
          <BrandLogo className="h-10 w-10" />
          <div>
            <p className="text-xs font-medium text-[#94A3B8] uppercase tracking-wider">
              LoversConnect Admin
            </p>
            <h2 className="text-base font-bold text-[#0F172A]">{title}</h2>
          </div>
        </div>
        <p className="text-sm font-medium leading-6 text-slate-600">{message}</p>
        <div className="mt-6 flex justify-end gap-3">
          <button
            className="rounded-lg border border-[#E2E8F0] px-4 py-2.5 text-sm font-semibold text-[#64748B] bg-[#F8FAFC] hover:bg-[#F1F5F9] transition-all"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            className="rounded-lg bg-[#D91F26] px-4 py-2.5 text-sm font-semibold text-white hover:bg-red-700 transition-all active:scale-[0.98]"
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function NavButton({ active, icon, label, onClick }) {
  return (
    <button
      className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
        active
          ? 'bg-[#D91F26]/15 text-white'
          : 'text-white/60 hover:text-white hover:bg-white/[0.08]'
      }`}
      style={active ? { boxShadow: 'inset 3px 0 0 0 #D91F26' } : undefined}
      onClick={onClick}
    >
      {icon}
      {label}
    </button>
  );
}
